import React from "react";
import { Pin, Sparkles } from "lucide-react";
import {
  Eye, BookOpen, Anchor, GitBranch, Search, Scroll, UserCheck, Target,
  Scale, Languages, Key, Compass, Users, Crown, Moon, ShieldAlert, Map,
  FunctionSquare, Telescope, FolderHeart, Ghost, VenetianMask, UserCog,
  MessageCircleOff, Gavel, BadgeCheck, Link2, Hammer, Brain, MessageSquare,
} from "lucide-react";

const ICONS = {
  "eye": Eye, "book-open": BookOpen, "anchor": Anchor, "git-branch": GitBranch,
  "search": Search, "scroll": Scroll, "user-check": UserCheck, "target": Target,
  "sparkles": Sparkles, "scale": Scale, "languages": Languages, "key": Key,
  "compass": Compass, "users": Users, "crown": Crown,
  "moon": Moon, "shield-alert": ShieldAlert, "map": Map,
  "function-square": FunctionSquare, "telescope": Telescope,
  "folder-heart": FolderHeart, "ghost": Ghost, "venetian-mask": VenetianMask,
  "user-cog": UserCog, "message-circle-off": MessageCircleOff,
  "gavel": Gavel, "badge-check": BadgeCheck, "link": Link2,
  "hammer": Hammer, "brain": Brain, "message-square": MessageSquare,
};

/**
 * PinnedSigilRow — surfaces 1-3 pinned sigils at the top of a user's profile.
 *
 * - 1 sigil: renders large (same tall card as the legacy single-pin version)
 * - 2-3 sigils: renders a horizontal row of medium cards, each glowing in its
 *   own color. The row reads left→right as slots 1→3.
 *
 * Accepts a `sigils` array. Falls back to the legacy `sigil` prop for any
 * callers still using single-pin.
 */
export default function PinnedSigilRow({ sigils, sigil }) {
  const items = Array.isArray(sigils) && sigils.length > 0
    ? sigils
    : (sigil ? [sigil] : []);
  if (items.length === 0) return null;

  if (items.length === 1) {
    return <BigCard sigil={items[0]} />;
  }
  return (
    <div
      data-testid="pinned-sigil-row"
      className="grid grid-cols-2 md:grid-cols-3 gap-3 h-full"
    >
      {items.map((s) => <MediumCard key={s.id} sigil={s} />)}
    </div>
  );
}

function BigCard({ sigil }) {
  const Icon = ICONS[sigil.icon] || Sparkles;
  const color = sigil.color || "#a78bfa";
  return (
    <div
      data-testid="pinned-sigil-badge"
      className="relative border-2 p-5 sm:p-6 overflow-hidden h-full flex flex-col"
      style={{
        borderColor: `${color}88`,
        background: `radial-gradient(circle at 20% 30%, ${color}1a, transparent 65%), #0a0a0a`,
        boxShadow: `0 0 0 1px ${color}33, 0 24px 60px -28px ${color}66`,
      }}
    >
      <div className="absolute -right-8 -top-8 opacity-[0.09]" style={{ color }}>
        <Icon size={200} />
      </div>
      <div className="relative flex items-center gap-1.5 font-mono text-[10px] tracking-[0.25em]" style={{ color }}>
        <Pin size={11} /> PINNED SIGIL
      </div>
      <div className="relative flex items-center gap-4 flex-1 mt-3">
        <div
          className="shrink-0 p-3 flex items-center justify-center"
          style={{ backgroundColor: `${color}22`, color }}
        >
          <Icon size={40} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black uppercase tracking-tight text-2xl sm:text-3xl text-white truncate">
            {sigil.name}
          </div>
          <div className="text-zinc-400 text-xs sm:text-sm mt-1">
            {sigil.description}
          </div>
        </div>
      </div>
    </div>
  );
}

function MediumCard({ sigil }) {
  const Icon = ICONS[sigil.icon] || Sparkles;
  const color = sigil.color || "#a78bfa";
  return (
    <div
      data-testid={`pinned-sigil-medium-${sigil.id}`}
      className="relative border p-3 overflow-hidden h-full flex flex-col"
      style={{
        borderColor: `${color}77`,
        background: `radial-gradient(circle at 30% 30%, ${color}1a, transparent 65%), #0a0a0a`,
        boxShadow: `0 0 0 1px ${color}1a, 0 18px 48px -22px ${color}55`,
      }}
    >
      <div className="absolute -right-6 -bottom-6 opacity-[0.08]" style={{ color }}>
        <Icon size={120} />
      </div>
      {/* Top row: PINNED label + icon chip */}
      <div className="relative flex items-center justify-between gap-2">
        <div className="font-mono text-[8px] tracking-[0.25em] flex items-center gap-1" style={{ color }}>
          <Pin size={8} /> PINNED
        </div>
        <div
          className="shrink-0 p-1.5"
          style={{ backgroundColor: `${color}22`, color }}
        >
          <Icon size={18} />
        </div>
      </div>
      {/* Bottom: name + description pinned to the bottom via mt-auto so the
          card visually fills its allocated height */}
      <div className="relative mt-auto pt-3">
        <div className="font-heading font-black uppercase tracking-tight text-sm text-white leading-tight">
          {sigil.name}
        </div>
        <div className="font-mono text-[9px] tracking-[0.18em] text-zinc-500 mt-1.5 leading-snug line-clamp-3">
          {sigil.description}
        </div>
      </div>
    </div>
  );
}
